import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/motion";
import { SearchBar } from "@/components/SearchBar";
import { PropertyCard } from "@/components/PropertyCard";
import { api } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const tabDefs = [
  { key: "all", params: {} },
  { key: "vendita", params: { contract: "vendita" } },
  { key: "affitto", params: { contract: "affitto" } },
  { key: "prestigio", params: { category: "prestigio" } },
  { key: "commerciali", params: { category: "commerciali" } },
  { key: "cantieri", params: { category: "cantieri" } },
];

const tabLabels = (t, key) =>
  ({ all: t("search.tutti"), vendita: t("nav.vendita"), affitto: t("nav.affitto"), prestigio: t("nav.prestigio"), commerciali: t("nav.commerciali"), cantieri: t("nav.cantieri") })[key];

const ListingsPage = () => {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    setLoading(true);
    const query = {};
    ["contract", "category", "zone", "min_price", "max_price", "min_mq", "rooms", "q", "featured"].forEach((k) => {
      const v = params.get(k);
      if (v) query[k] = v;
    });
    api.get("/properties", { params: query })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [params]);

  const activeTab = (tb) => {
    const entries = Object.entries(tb.params);
    if (entries.length === 0) return !params.get("contract") && !params.get("category") && !params.get("featured");
    return entries.every(([k, v]) => params.get(k) === v);
  };

  const setTab = (tb) => {
    const next = new URLSearchParams();
    Object.entries(tb.params).forEach(([k, v]) => next.set(k, v));
    setParams(next);
  };

  return (
    <div data-testid="listings-page" className="pt-36 sm:pt-40 pb-24">
      <Seo
        title={t("seo.listTitle")}
        siteName="ROMACASA"
        description={t("seo.listDesc")}
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Eyebrow>{t("list.portfolio")}</Eyebrow>
          <h1 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight mt-4 mb-10">{t("list.title")}</h1>
        </Reveal>

        <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="category-tabs">
          {tabDefs.map((tb) => (
            <button
              key={tb.key}
              data-testid={`tab-${tb.key}`}
              onClick={() => setTab(tb)}
              className={`px-5 py-2.5 text-sm font-medium border transition-all duration-300 ${
                activeTab(tb)
                  ? "bg-[#000000] text-[#FAFAF7] border-[#000000]"
                  : "border-[#E0E3E6] text-[#5B6670] hover:border-[#000000] hover:text-[#000000]"
              }`}
            >
              {tabLabels(t, tb.key)}
            </button>
          ))}
          <button
            data-testid="toggle-filters-button"
            onClick={() => setShowFilters(!showFilters)}
            className="ml-auto flex items-center gap-2 px-5 py-2.5 text-sm font-medium border border-[#000000] text-[#000000] hover:bg-[#000000] hover:text-white transition-all duration-300"
          >
            {showFilters ? <X size={15} /> : <SlidersHorizontal size={15} />} {t("list.filtri")}
          </button>
        </div>

        {showFilters && (
          <div className="mb-10">
            <SearchBar compact />
          </div>
        )}

        {loading ? (
          <div className="py-24 text-center text-[#5B6670] font-light" data-testid="listings-loading">{t("list.loading")}</div>
        ) : items.length === 0 ? (
          <div className="py-24 text-center" data-testid="listings-empty">
            <p className="font-serif-d text-3xl text-[#000000] mb-3">{t("list.empty")}</p>
            <p className="text-[#5B6670] font-light mb-8 max-w-lg mx-auto">{t("list.emptyText")}</p>
            <Link to="/contatti" data-testid="empty-contact-link" className="inline-block bg-[#000000] hover:bg-[#000000] text-white px-7 py-3.5 text-sm font-medium transition-colors">{t("list.contattaci")}</Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#5B6670] mb-8 font-mono-d" data-testid="listings-count">
              {items.length} {items.length === 1 ? t("list.one") : t("list.many")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((p, i) => (
                <Reveal key={p.id} delay={Math.min(i, 5) * 0.06}>
                  <PropertyCard p={p} />
                </Reveal>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ListingsPage;
